import type { IconName } from '@similarweb/icons';
import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import {
  StyledSectionHeader,
  StyledSectionHeaderTitle,
  StyledSectionHeaderSubtitle,
  StyledSectionHeaderInfo,
  StyledSectionHeaderInfoBlock,
  StyledSectionHeaderIconTooltip,
  StyledSectionHeaderIcon,
  StyledNewTag,
  StyledHistorycalDataContainer,
  StyledBetaTag,
  StyledSectionHeaderInfoIcon,
} from './styles';

export type SectionHeaderProps = {
  title: React.ReactNode;
  titleLink?: string;
  titleLinkTooltip?: string;
  infoIconTooltip?: string;
  onTitleLinkClick?: () => void;
  subtitle?: string;
  withSectionPadding?: boolean;
  dateInfo?: string;
  showWorldwideIcon?: boolean;
  websource?: 'all' | 'desktop' | 'mobile';
  HistorycalDataIconComponent?: React.ReactElement;
  isNew?: boolean;
  isBeta?: boolean;
  titleAdditionalComponent?: React.ReactElement;
};

const InfoBlock = ({ iconName, text }: { iconName: IconName; text: string }) => (
  <StyledSectionHeaderInfoBlock>
    <SWReactIcons size="xs" iconName={iconName} iconColor={colorsPalettes.carbon[200]} />
    {text}
  </StyledSectionHeaderInfoBlock>
);

export const SectionHeader = ({
  title,
  titleLink,
  titleLinkTooltip,
  infoIconTooltip,
  onTitleLinkClick,
  subtitle,
  withSectionPadding,
  dateInfo,
  showWorldwideIcon,
  websource,
  isNew,
  isBeta,
  HistorycalDataIconComponent,
  titleAdditionalComponent,
}: SectionHeaderProps) => {
  return (
    <StyledSectionHeader withSectionPadding={withSectionPadding}>
      <StyledSectionHeaderTitle>
        <span>{title}</span>
        {titleLink ? (
          <StyledSectionHeaderIconTooltip
            content={titleLinkTooltip}
            placement="top"
            minWidth={230}
            enabled={!!titleLinkTooltip}
          >
            <a href={titleLink} onMouseDown={onTitleLinkClick} target="_blank" rel="noreferrer">
              <StyledSectionHeaderIcon iconName="link-out" size="xs" />
            </a>
          </StyledSectionHeaderIconTooltip>
        ) : null}
        {infoIconTooltip ? (
          <StyledSectionHeaderIconTooltip content={infoIconTooltip} placement="top" minWidth={230}>
            <StyledSectionHeaderInfoIcon iconName="info" size="xs" />
          </StyledSectionHeaderIconTooltip>
        ) : null}
        {isNew ? <StyledNewTag /> : null}
        {isBeta ? <StyledBetaTag /> : null}
        {titleAdditionalComponent ? titleAdditionalComponent : null}
      </StyledSectionHeaderTitle>
      <StyledSectionHeaderInfo>
        {dateInfo ? <InfoBlock iconName="daily-ranking" text={dateInfo} /> : null}
        {showWorldwideIcon ? <InfoBlock iconName="global-rank" text="Worldwide" /> : null}
        {websource === 'all' ? <InfoBlock iconName="combined" text="All traffic" /> : null}
        {websource === 'desktop' ? <InfoBlock iconName="desktop" text="Desktop" /> : null}
        {websource === 'mobile' ? <InfoBlock iconName="mobile-web" text="Mobile Web" /> : null}
        {HistorycalDataIconComponent ? (
          <StyledHistorycalDataContainer>{HistorycalDataIconComponent}</StyledHistorycalDataContainer>
        ) : null}
      </StyledSectionHeaderInfo>
      {subtitle ? <StyledSectionHeaderSubtitle>{subtitle}</StyledSectionHeaderSubtitle> : null}
    </StyledSectionHeader>
  );
};
